import { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { H4Bold, ButtonIconBg } from '@comeon/ui';
import { useSelector, useDispatch } from 'react-redux';
import { Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { TracedRoutes } from '@comeon/error-reporter';
import { authenticationSelector } from '~/components/authentication/ducks/selectors.js';
import Icon, { ICON_OPTIONS_HORIZONTAL } from '~/components/icon/index.js';
import useContextualPathTransformer from '../../../../hooks/use-contextual-path-transformer.js';
import { LeftSideIconType } from '../../../sidebar/sidebar-header/sidebar-header.js';
import { Sidebar, SidebarHeader, SidebarContent, SidebarFooter } from '../../../sidebar/index.js';
import useIntervalAction from '../../../../hooks/use-interval-action.js';
import SidebarScrollAware from '~/utils/sidebar-scroll-aware.js';
import { isBiggerSelector } from '../../../layout/mediaquery.js';
import useTranslations from '../../../translation/use-translations';
import { ROUTE_CASINO_GAME, ROUTE_WE_SPIN } from '../../../router/constants.js';
import { WE_SPIN_ALL_TEXT_KEYS } from '../constants/we-spin-text-keys.js';
import {
	fetchSessions,
	fetchProfile,
	fetchPrivateSessions,
	fetchParticipations,
	FETCH_SESSIONS,
	FETCH_GAME_VIEW_EVENT
} from '../ducks/we-spin-duck.js';
import {
	selectSessionsLoaded,
	selectSessionForStreamRef,
	selectIsSessionOnline,
	selectConfiguration
} from '../ducks/we-spin-selectors.js';
import {
	ROUTE_WE_SPIN_GAME_ROOM_PREVIEW,
	ROUTE_WE_SPIN_GAME_ROOM_BUY_IN,
	ROUTE_WE_SPIN_GAME_ROOM_TIP,
	ROUTE_WE_SPIN_GAME_ROOM_STREAMER,
	ROUTE_WE_SPIN_GAME_ROOM_RULES,
	ROUTE_WE_SPIN_GAME_ROOM_EVENT_LIST,
	ROUTE_WE_SPIN_GAME_ROOM_CHAT,
	ROUTE_WE_SPIN_GAME_ROOM_INFO,
	ROUTE_WE_SPIN_GAME_VIEW,
	ROUTE_ACCOUNT_WE_SPIN_DASHBOARD,
	ROUTE_WE_SPIN_GAME_ROOM_BUY_IN_SUCCESS,
	ROUTE_WE_SPIN_GAME_ROOM_EVENT_NOT_FOUND,
	ROUTE_WE_SPIN_GAME_ROOM_ABS
} from '../constants/we-spin-route-constants.js';
import {
	BUY_IN_STATES,
	REGISTRATION,
	RUNNING,
	SESSION_OFFLINE,
	SESSION_ONLINE,
	ENDED
} from '../constants/we-spin-stream-states.js';
import { streamToGameRoomRoute } from '../utils/stream-to-game-room-route.js';
import WeSpinGameRoomPreview from './we-spin-game-room-preview.js';
import WeSpinGameRoomBuyIn from './we-spin-game-room-buy-in.js';
import WeSpinGameRoomEventList from './we-spin-game-room-event-list.js';
import WeSpinGameRoomEventNotFound from './we-spin-game-room-event-not-found.js';
import WeSpinGameRoomFooter from './we-spin-game-room-footer.js';
import StreamElement from './stream-element.js';
import WeSpinGameRoomStreamer from './we-spin-game-room-streamer.js';
import WeSpinGameRoomSidebarModal from './we-spin-game-room-sidebar-modal.js';
import WeSpinGameRoomRules from './we-spin-game-room-rules.js';
import WE_SPIN_GAME_ROOM_TAB_ITEMS from './we-spin-game-room-tab-items.js';
import StartTime from '../components/start-time.js';
import useWeSpinGATracking from '../components/use-we-spin-ga-tracking.js';
import { eventTagDescriptionText } from '../constants/we-spin-event-tags.js';
import { WE_SPIN_CONTROLLER_HEAD_TITLE_DATA_AT } from '../constants/we-spin-data-at-attributes.js';
import { RESOURCE_KEYS } from '../constants/we-spin-resource-keys.js';
import useResources from '../../../resource/use-resources.js';
import { getWeekDayDateAndMonth } from '../utils/we-spin-time-helper.js';
import generateSrcSet from '../../../../utils/generateSrcSet.js';
import { useCloseCurrentContext } from '~/hooks/use-close-context';
import Loading from '../../../misc/loading.js';
import { fetchCurrentEvent, selectCurrentEvent, setCurrentEventRef } from '../ducks/we-spin-home-game-duck.js';
import WeSpinGameRoomBuyInSuccess from './we-spin-game-room-buy-in-success.js';

import {
	fetchFollowedStreamersList,
	setCurrentStreamerRef,
	fetchCurrentStreamerProfile,
	selectCurrentStreamerRef
} from '../ducks/we-spin-streamer-duck.js';
import styled from 'styled-components';
import { selectLiveEvent } from '../ducks/we-spin-dashboard-duck.js';

import { getRelativePaths } from '~/utils/pathname';

const DESKTOP_ROUTES_WITH_STREAM = [
	ROUTE_WE_SPIN_GAME_ROOM_PREVIEW,
	ROUTE_WE_SPIN_GAME_ROOM_BUY_IN,
	ROUTE_WE_SPIN_GAME_ROOM_EVENT_LIST,
	ROUTE_WE_SPIN_GAME_ROOM_BUY_IN_SUCCESS
];

const CHILD_ROUTES = [
	ROUTE_WE_SPIN_GAME_ROOM_BUY_IN_SUCCESS,
	ROUTE_WE_SPIN_GAME_ROOM_PREVIEW,
	ROUTE_WE_SPIN_GAME_ROOM_BUY_IN,
	ROUTE_WE_SPIN_GAME_ROOM_STREAMER,
	ROUTE_WE_SPIN_GAME_ROOM_EVENT_LIST,
	ROUTE_WE_SPIN_GAME_ROOM_EVENT_NOT_FOUND,
	ROUTE_WE_SPIN_GAME_ROOM_RULES,
	ROUTE_WE_SPIN_GAME_ROOM_EVENT_LIST,
	ROUTE_WE_SPIN_GAME_ROOM_CHAT,
	ROUTE_WE_SPIN_GAME_ROOM_INFO,
	ROUTE_WE_SPIN_GAME_ROOM_TIP
];

const SIDEBAR_TITLE_SCROLL_THRESHOLD = 30;

const LiveEventBadge = styled.div`
	position: absolute;
	bottom: -12px;
	left: 50%;
	-webkit-transform: translateX(-50%);
	transform: translateX(-50%);
	padding: 4px 12px;
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 3px;
	background-color: var(--search-result-item-bg-secondary);
	font-weight: 400;
	font-size: 12px;
	line-height: 16px;
	cursor: default;
`;

const LiveEventLink = styled.a`
	color: rgba(var(--primary), 1);
`;
// TODO: FIX ME, the streamRef should be read from inside the components, instead of reading it hacky way like this and pass it down
// TODO: keep the gateway logic minimum, just matching routes, the api calls, effects can moved to another top level component like a layout component
const WeSpinGameRoomSidebarController = () => {
	const params = useParams();
	// TODO: hacky way to read the 2nd path param which is the ref, using the splat, *, which gives the entire path param and reading the ref
	const streamRef = useMemo(() => params['*']?.split('/')[1], [params]);
	const location = useLocation();
	// TODO: can do we need to check path like this at the gateway level
	const path = location?.pathname;
	const navigate = useNavigate();
	const { translations, isLoadingTranslations } = useTranslations(WE_SPIN_ALL_TEXT_KEYS);
	const { resources, isLoading: isLoadingResources } = useResources(RESOURCE_KEYS);
	const transformPath = useContextualPathTransformer();
	const { wespinChatDisabled } = useSelector(selectConfiguration);
	const [openModal, setOpenModal] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const { weSpinViewListPollInterval, hasWeSpinTipping, hasWeSpinStreamerFollowers, hasWeSpinFriends } =
		useSelector(selectConfiguration);

	const isAuthenticated = useSelector(authenticationSelector);

	const buttonRef = useRef();
	const dispatch = useDispatch();
	useEffect(() => {
		setIsLoading(true);
		dispatch(fetchProfile());
		dispatch(setCurrentEventRef(streamRef));
		dispatch(fetchCurrentEvent()).then(() => {
			setIsLoading(false);
		});
	}, [dispatch, streamRef]);

	const isBigger = useSelector(isBiggerSelector);
	const streamsLoaded = useSelector(selectSessionsLoaded);
	const streamView = useSelector(selectCurrentEvent);
	const session = useSelector(selectSessionForStreamRef(streamRef));
	const isSessionOnline = useSelector(selectIsSessionOnline(streamRef)); // streamRef === sessionRef
	const currentStreamerRef = useSelector(selectCurrentStreamerRef);

	const liveEvent = useSelector(selectLiveEvent);
	const liveEventName = liveEvent
		? liveEvent?.currentGame?.gameName || liveEvent?.gameDescs?.[0]?.gameName || liveEvent?.name
		: '';

	// TODO: lot of side effect API calls that keep re-rendering the routes, check if this is necessary
	useIntervalAction(
		FETCH_SESSIONS,
		() => {
			dispatch(fetchSessions());
			if (hasWeSpinFriends) {
				dispatch(fetchPrivateSessions());
			}
		},
		weSpinViewListPollInterval || 5000
	);
	useIntervalAction(
		FETCH_GAME_VIEW_EVENT,
		() => {
			dispatch(fetchCurrentEvent());
			dispatch(fetchParticipations({ forcedUpdate: true }));
		},
		weSpinViewListPollInterval || 5000
	);

	useWeSpinGATracking(streamRef);

	useEffect(() => {
		if (streamView?.streamerRef && streamView?.streamerRef !== currentStreamerRef) {
			dispatch(setCurrentStreamerRef(streamView?.streamerRef));
			dispatch(fetchCurrentStreamerProfile());
		}
	}, [dispatch, streamView, currentStreamerRef]);

	const streamViewRef = streamView?.streamRef;
	useEffect(() => {
		if (streamViewRef && isAuthenticated && hasWeSpinStreamerFollowers) {
			dispatch(fetchFollowedStreamersList());
		}
	}, [dispatch, streamViewRef, isAuthenticated, hasWeSpinStreamerFollowers]);

	const closeIsVisible =
		!isBigger ||
		(session && path.startsWith(ROUTE_WE_SPIN_GAME_ROOM_STREAMER)) ||
		(path.startsWith(ROUTE_WE_SPIN_GAME_ROOM_EVENT_LIST) &&
			!location.pathname.startsWith(ROUTE_WE_SPIN_GAME_VIEW)) || // Buy in but if private event only
		(path.startsWith(ROUTE_WE_SPIN_GAME_ROOM_BUY_IN) && !!streamView?.isPrivateStreamer);

	const backButtonVisible =
		path.startsWith(ROUTE_WE_SPIN_GAME_ROOM_RULES) ||
		// Buy in but if not private event only
		(path.startsWith(ROUTE_WE_SPIN_GAME_ROOM_BUY_IN) && !streamView?.isPrivateStreamer) ||
		path.startsWith(ROUTE_WE_SPIN_GAME_ROOM_PREVIEW);

	const headerIcon = backButtonVisible
		? LeftSideIconType.BACK_ICON_BG
		: closeIsVisible
		? LeftSideIconType.CLOSE_ICON_BG
		: LeftSideIconType.NONE;

	const redirectedFromDashboard = useMemo(() => location.state?.fromDashboard, [location.state]);

	const closeCurrentContext = useCloseCurrentContext();
	const onLeftSideButtonClick = useMemo(
		() =>
			backButtonVisible
				? () =>
						redirectedFromDashboard
							? navigate(transformPath(ROUTE_ACCOUNT_WE_SPIN_DASHBOARD))
							: navigate(-1)
				: closeIsVisible
				? () => (isBigger ? closeCurrentContext() : navigate(ROUTE_WE_SPIN, { replace: true }))
				: undefined,
		[
			closeIsVisible,
			backButtonVisible,
			navigate,
			isBigger,
			closeCurrentContext,
			transformPath,
			redirectedFromDashboard
		]
	);

	const onClickHandlerForThreeDots = useCallback(() => {
		setOpenModal(true);
	}, [setOpenModal]);
	const hideModalCallback = useCallback(() => {
		setOpenModal(false);
	}, [setOpenModal]);

	const FILTERED_TAB_ITEMS = useMemo(
		() =>
			WE_SPIN_GAME_ROOM_TAB_ITEMS.filter(
				({ link }) =>
					hasWeSpinTipping ||
					[
						ROUTE_WE_SPIN_GAME_ROOM_TIP,
						...(wespinChatDisabled ? [ROUTE_WE_SPIN_GAME_ROOM_CHAT] : [])
					].indexOf(link) === -1
			),
		[hasWeSpinTipping, wespinChatDisabled]
	);
	const gotoDefaultView = useCallback(
		() => navigate(transformPath(`${ROUTE_WE_SPIN_GAME_ROOM_EVENT_LIST}/${streamRef}`), { replace: true }),
		[navigate, streamRef, transformPath]
	);

	// get relative paths of child routes to we spin game room
	const paths = useMemo(() => getRelativePaths(CHILD_ROUTES, ROUTE_WE_SPIN_GAME_ROOM_ABS), []);

	if (isLoadingTranslations || isLoadingResources || !streamsLoaded || !streamView) return null;

	const {
		streamerName,
		state: eventState,
		joined,
		games,
		start,
		description,
		name,
		eventTags = [],
		boost,
		streamBgUrl,
		streamerImgUrl
	} = streamView;

	// Take current game and filter out empty game result as {gameCode: "",gameImageUrl: "Not found",gameName: "Or enter them manually here"}
	const currentGame = streamView?.currentGame?.gameCode ? streamView?.currentGame : null;

	const gameImageUrl = currentGame?.gameImageUrl;

	const isBuyInOrPreviewView =
		path.startsWith(ROUTE_WE_SPIN_GAME_ROOM_PREVIEW) || path.startsWith(ROUTE_WE_SPIN_GAME_ROOM_BUY_IN);

	const isNotificationRoute = path.startsWith(ROUTE_WE_SPIN_GAME_ROOM_BUY_IN_SUCCESS);

	const hasEventTag = eventTags.length > 0;

	const topText = isBuyInOrPreviewView ? (
		games?.[0]?.gameName || name
	) : (
		<StartTime
			state={
				streamView.state === SESSION_ONLINE
					? RUNNING
					: streamView.state === SESSION_OFFLINE
					? REGISTRATION
					: ENDED
			}
			start={start}
			isOnline={isSessionOnline}
		/>
	);

	const isInGameRoom = location.pathname.startsWith(ROUTE_WE_SPIN_GAME_VIEW);
	const isDesktopRouteWithStream = DESKTOP_ROUTES_WITH_STREAM.some(r => path.startsWith(r));

	const backgroundImage = streamBgUrl || gameImageUrl || streamerImgUrl || null;
	// const backgroundImage =
	// isBuyInOrPreviewView && !streamView.isPrivateStreamer && !!gameImageUrl
	// 	? gameImageUrl
	// 	: streamerImgUrl || streamBgUrl || null;

	const backgroundImageSrcSet = backgroundImage ? generateSrcSet(backgroundImage, true) : null;
	const headerText = isBuyInOrPreviewView ? getWeekDayDateAndMonth(start, translations) : name;

	const descriptionText = isBuyInOrPreviewView
		? hasEventTag
			? eventTagDescriptionText(eventTags[0], translations)
			: description
		: description;

	const isBiggerAndDesktopInGameRoomWithStream = isBigger && isInGameRoom && isDesktopRouteWithStream;

	return (
		<>
			{/* Notifications, views contain entire sidebar declaration but not only body */}
			{!!isNotificationRoute && (
				<TracedRoutes>
					<Route
						path={`${paths[ROUTE_WE_SPIN_GAME_ROOM_BUY_IN_SUCCESS]}/:ref`}
						element={
							<WeSpinGameRoomBuyInSuccess
								streamView={streamView}
								streamRef={streamRef}
								resources={resources}
								translations={translations}
							/>
						}
					/>
				</TracedRoutes>
			)}
			{/* Regular logic with flexible header/footer and body depends on route */}
			{!isNotificationRoute && (
				<Sidebar className="wespin-sidebar">
					<SidebarScrollAware>
						{scrollYPosition => (
							<SidebarHeader
								transparent={scrollYPosition < SIDEBAR_TITLE_SCROLL_THRESHOLD}
								leftSideIcon={headerIcon}
								onLeftSideButtonClick={onLeftSideButtonClick}
								className="transparent_title"
								opaqueClassName="page-head--opac"
							>
								{!path.startsWith(ROUTE_WE_SPIN_GAME_ROOM_RULES) && (
									<H4Bold
										className="page-head__title wespin-gameroom-title"
										data-at={WE_SPIN_CONTROLLER_HEAD_TITLE_DATA_AT}
									>
										{streamerName}
									</H4Bold>
								)}

								<div className="page-head__right-side">
									{!path.startsWith(ROUTE_WE_SPIN_GAME_ROOM_RULES) && (
										<ButtonIconBg
											onClick={onClickHandlerForThreeDots}
											className="dots-container dots-container--90deg u-cursor-hand u-mr-1"
										>
											<Icon type={ICON_OPTIONS_HORIZONTAL} />
										</ButtonIconBg>
									)}
								</div>
								{!!liveEvent &&
									(streamRef !== liveEvent?.streamRef ||
										path.startsWith(ROUTE_WE_SPIN_GAME_ROOM_STREAMER)) && (
										<LiveEventBadge>
											{translations.WE_SPIN_LIVE_EVENT_LINK_TITLE}
											&nbsp;
											<LiveEventLink
												href={`${ROUTE_CASINO_GAME}/${streamView.currentGame.gameCode}`}
												target="_blank"
												rel="noopener noreferrer"
											>
												{liveEventName}
											</LiveEventLink>
										</LiveEventBadge>
									)}
							</SidebarHeader>
						)}
					</SidebarScrollAware>
					<SidebarContent>
						{!isLoading && streamView?.streamRef === streamRef && (
							<>
								{(!isBigger || isDesktopRouteWithStream) && !isNotificationRoute && (
									<div className="top-of-page-image-wrapper u-relative">
										<StreamElement
											size="small"
											translations={translations}
											type={
												isBiggerAndDesktopInGameRoomWithStream || isBuyInOrPreviewView
													? 'IMAGE'
													: isSessionOnline
													? 'STREAM'
													: 'IMAGE'
											}
											streamLinkUrl={
												isBiggerAndDesktopInGameRoomWithStream || isBuyInOrPreviewView
													? backgroundImage
													: streamView.streamUrl
											}
											topText={topText}
											headerText={headerText}
											streamerDescription={descriptionText}
											boost={boost}
											showBoost={isBuyInOrPreviewView}
											srcSet={backgroundImageSrcSet}
											streamView={streamView}
											showEventTagBg={isBuyInOrPreviewView && hasEventTag}
										/>
									</div>
								)}
								<TracedRoutes>
									{joined && (
										<Route
											path={`${paths[ROUTE_WE_SPIN_GAME_ROOM_PREVIEW]}/:ref`}
											element={
												<WeSpinGameRoomPreview streamView={streamView} streamRef={streamRef} />
											}
										/>
									)}
									{/* Buy in available only if not bought in yet */}
									{BUY_IN_STATES.includes(eventState) && !joined && (
										<Route
											path={`${paths[ROUTE_WE_SPIN_GAME_ROOM_BUY_IN]}/:ref`}
											element={
												<WeSpinGameRoomBuyIn
													streamView={streamView}
													streamRef={streamRef}
													buttonRef={buttonRef}
												/>
											}
										/>
									)}
									<Route
										path={`${paths[ROUTE_WE_SPIN_GAME_ROOM_STREAMER]}/:ref`}
										element={
											<WeSpinGameRoomStreamer
												streamView={streamView}
												streamRef={streamRef}
												resources={resources}
											/>
										}
									/>
									<Route
										path={`${paths[ROUTE_WE_SPIN_GAME_ROOM_EVENT_LIST]}/:ref`}
										element={
											<WeSpinGameRoomEventList
												streamView={streamView}
												streamRef={streamRef}
												resources={resources}
											/>
										}
									/>
									<Route
										path={`${paths[ROUTE_WE_SPIN_GAME_ROOM_EVENT_NOT_FOUND]}/:ref`}
										element={<WeSpinGameRoomEventNotFound />}
									/>
									{FILTERED_TAB_ITEMS.map(({ Component, link }) => (
										<Route
											key={link}
											path={`${paths[link]}/:ref`}
											element={
												<Component
													streamView={streamView}
													streamRef={streamRef}
													gotoDefaultView={gotoDefaultView}
													buttonRef={buttonRef}
												/>
											}
										/>
									))}
									<Route
										path={`${paths[ROUTE_WE_SPIN_GAME_ROOM_RULES]}/:ref`}
										element={<WeSpinGameRoomRules />}
									/>
									<Route
										path="*"
										element={
											<Navigate to={transformPath(streamToGameRoomRoute(streamView))} replace />
										}
									/>
								</TracedRoutes>
							</>
						)}
						{/* Loading */}
						{isLoading && <Loading />}
					</SidebarContent>
					<SidebarFooter>
						<WeSpinGameRoomFooter streamView={streamView} ref={buttonRef} location={location} />
					</SidebarFooter>
				</Sidebar>
			)}
			{/* Menu modal */}
			<WeSpinGameRoomSidebarModal
				streamView={streamView}
				openModal={openModal}
				hideModalCallback={hideModalCallback}
			/>
		</>
	);
};

export default WeSpinGameRoomSidebarController;
